<template>
  <div class="ai-build-page">
    <b-navbar type="dark" variant="primary" id="context-nav">
      <span class="ai-build-context">Ask about this app, or build and style it in one conversation</span>
      <modal-help topic="ai-build" label="Help for this screen" show-label />
    </b-navbar>

    <div class="ai-build-body">
      <div class="ai-build-sidebar">
        <div class="sidebar-header">
          <div class="sidebar-section-label">Chats</div>
          <button class="new-chat-btn" @click="newChat" title="New chat">+</button>
        </div>
        <div class="ai-build-history">
          <div
            v-for="entry in history"
            :key="entry._id"
            class="ai-build-history-item"
            :class="{ 'ai-build-history-item-active': activeHistoryId === entry._id }"
            @click="loadHistoryEntry(entry._id)"
          >
            <div class="ai-build-history-prompt">{{ entry.prompt }}</div>
            <div class="ai-build-history-meta">
              <span>{{ entry.turnCount }} turn{{ entry.turnCount === 1 ? '' : 's' }}</span>
              <span>{{ formatDate(entry.createdAt) }}</span>
            </div>
            <button class="ai-build-history-delete" @click.stop="deleteHistoryEntry(entry._id)" title="Delete">&times;</button>
          </div>
          <div v-if="!history.length" class="ai-build-history-empty">No chats yet</div>
        </div>
        <button v-if="history.length" class="ai-build-clear" @click="clearHistory">clear chats</button>
      </div>

      <div class="ai-build-main">
        <div class="ai-build-thread" ref="thread">
          <div v-if="!conversation.length && !building" class="ai-build-empty">
            <p>Ask how this app is put together, or describe what to build. Paste a screenshot or a link when you want the builder to look at it. Questions stay as conversation. When a change needs a decision, the builder asks before it edits anything. After it builds a page, it looks at that page and can adjust what it sees.</p>
            <div class="ai-build-examples">
              <button v-for="example in examples" :key="example" type="button" @click="prompt = example">{{ example }}</button>
            </div>
          </div>

          <div v-for="(turn, index) in conversation" :key="index" class="ai-build-turn">
            <div class="ai-build-user">
              <div class="ai-build-label">You</div>
              <div class="ai-build-user-text">{{ turn.prompt }}</div>
              <div v-if="turn.images && turn.images.length" class="ai-build-user-shots">
                <img
                  v-for="(image, imageIndex) in turn.images"
                  :key="imageIndex"
                  :src="imageSrc(image)"
                  alt="Screenshot attached to the prompt"
                >
              </div>
            </div>
            <div v-if="turn.answer !== null || turn.thinking || turn.status || (building && index === conversation.length - 1)" class="ai-build-assistant">
              <div class="ai-build-label ai-build-label-ai">AI</div>
              <details v-if="turn.thinking" open class="ai-build-thoughts">
                <summary>Thoughts</summary>
                <pre ref="thoughtBox">{{ turn.thinking }}</pre>
              </details>
              <div v-if="turn.status || (building && index === conversation.length - 1)" class="ai-build-loading">
                <div class="ai-build-spinner"></div>
                <span>{{ turn.status || 'Thinking...' }}</span>
              </div>
              <div v-if="turn.actions && turn.actions.length" class="ai-build-actions">
                <div
                  v-for="(action, actionIndex) in turn.actions"
                  :key="actionIndex"
                  class="ai-build-action"
                  :class="{ 'ai-build-action-failed': !action.ok }"
                >
                  <span class="ai-build-action-tool">{{ actionLabel(action.tool) }}</span>
                  <span>{{ action.summary }}</span>
                  <img
                    v-if="action.image"
                    class="ai-build-shot"
                    :src="'data:image/jpeg;base64,' + action.image"
                    alt="Screenshot of the page"
                  >
                </div>
              </div>
              <div v-if="turn.answer" class="ai-build-answer">{{ turn.answer }}</div>
            </div>
          </div>
        </div>

        <div v-if="error" class="ai-build-error">
          <span>{{ error }}</span>
          <button type="button" @click="error = null">&times;</button>
        </div>

        <div class="ai-build-prompt">
          <div v-if="attachments.length" class="ai-build-attachments">
            <div v-for="(image, index) in attachments" :key="index" class="ai-build-attachment">
              <img :src="imageSrc(image)" alt="Screenshot to send">
              <button type="button" @click="removeAttachment(index)" title="Remove screenshot">&times;</button>
            </div>
          </div>
          <div class="ai-build-prompt-row">
            <textarea
              v-model="prompt"
              :placeholder="conversation.length ? 'Ask a question, describe a change, or paste a link...' : 'Ask about this app, describe what to build, or paste a link...'"
              rows="3"
              :disabled="building"
              @paste="onPromptPaste"
              @keydown.ctrl.enter="send"
              @keydown.meta.enter="send"
            ></textarea>
            <div class="ai-build-prompt-buttons">
              <button type="button" class="ai-build-attach" :disabled="building" @click="openShotPicker">Add screenshot</button>
              <input ref="shotInput" class="ai-build-file" type="file" accept="image/*" multiple @change="onShotFiles">
              <button type="button" class="ai-build-send" :disabled="building || !canSend" @click="send">
                {{ building ? 'Sending...' : 'Send' }}
              </button>
            </div>
          </div>
          <AiTokenUsage />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AiTokenUsage from '../AiTokenUsage.vue'
import { publishAiTokenUsage } from '../../lib/ai-token-usage.js'
const ACTION_LABELS = {
  list_app: 'app',
  see_page: 'look',
  see_url: 'link',
  create_spreadsheet: 'spreadsheet',
  add_columns: 'columns',
  create_page: 'page',
  edit_view: 'view',
  add_view: 'view',
  set_app: 'settings',
  move_page: 'page',
  delete_page: 'page',
  delete_view: 'view',
  delete_spreadsheet: 'spreadsheet',
  delete_column: 'column',
  style_app: 'app style',
  style_page: 'page style',
  style_view: 'view style'
}

export default {
  name: 'AiBuild',
  components: { AiTokenUsage },
  data () {

    return {
      prompt: '',
      attachments: [],
      building: false,
      error: null,
      conversation: [],
      sessionMessages: [],
      activeHistoryId: null,
      reviewAbort: null,
      history: [],
      examples: [
        'What spreadsheets, columns, and pages are in this app?',
        'Create a contacts spreadsheet with name, email, and phone, and a home page with a table and a form.',
        'Style the app with a dark background and green accents.'
      ]
    }
  },
  computed: {
    canSend () {

      return !!this.prompt.trim() || this.attachments.length > 0
    }
  },
  mounted () {

    this.loadHistory()
  },
  watch: {
    '$route.params.application' () {

      this.history = []
      this.newChat()
      this.loadHistory()
    }
  },
  methods: {
    headers () {

      return { 'application-unique-title': this.$route.params.application }
    },
    actionLabel (tool) {

      return ACTION_LABELS[tool] || tool || 'step'
    },
    attachScreenshot (turn, payload) {

      if (!payload || !payload.image) return

      let index = -1

      for (let i = turn.actions.length - 1; i >= 0; i--) {

        if ((turn.actions[i].tool === 'see_page' || turn.actions[i].tool === 'see_url') && !turn.actions[i].image) {

          index = i
          break
        }
      }

      if (index < 0) return

      turn.actions.splice(index, 1, Object.assign({}, turn.actions[index], { image: payload.image }))
    },
    keepShots (previous, next) {

      return (next || []).map((action, index) => {

        const image = previous[index] && previous[index].image

        if (!image) return action

        return Object.assign({}, action, { image })
      })
    },
    assistantContent (turn) {

      const lines = []

      if (turn.thinking) {

        const text = turn.thinking.length > 4000 ? turn.thinking.slice(-4000) : turn.thinking

        lines.push(`Earlier thinking:\n${text}`)
      }

      for (const action of turn.actions || []) {

        lines.push(`${action.ok ? 'Done' : 'Failed'} ${action.tool}: ${action.summary}`)
      }

      if (turn.answer) lines.push(turn.answer)

      return lines.join('\n')
    },
    async loadHistory () {

      try {

        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build/history`,
          { headers: this.headers() }
        )

        this.history = response.data
      } catch (error) {

        console.error('Failed to load build history:', error)
      }
    },
    async loadHistoryEntry (id) {

      try {

        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build/history/${id}`,
          { headers: this.headers() }
        )
        const entry = response.data
        const conversation = entry.conversation || [{
          prompt: entry.prompt,
          answer: entry.answer,
          actions: entry.actions || []
        }]

        this.conversation = conversation
        this.sessionMessages = []

        for (const turn of conversation) {

          this.sessionMessages.push({ role: 'user', content: turn.prompt })
          this.sessionMessages.push({ role: 'assistant', content: this.assistantContent(turn) })
        }

        this.activeHistoryId = id
        this.error = null
        this.prompt = ''
        this.scrollToEnd()
      } catch (error) {

        console.error('Failed to load build chat:', error)
      }
    },
    async deleteHistoryEntry (id) {

      try {

        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build/history/${id}`,
          { headers: this.headers() }
        )

        this.history = this.history.filter((entry) => entry._id !== id)

        if (this.activeHistoryId === id) this.newChat()
      } catch (error) {

        console.error('Failed to delete build chat:', error)
      }
    },
    async clearHistory () {

      try {

        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build/history`,
          { headers: this.headers() }
        )

        this.history = []
        this.newChat()
      } catch (error) {

        console.error('Failed to clear build chats:', error)
      }
    },
    newChat () {

      this.stopReview()
      this.conversation = []
      this.sessionMessages = []
      this.activeHistoryId = null
      this.prompt = ''
      this.attachments = []
      this.error = null
    },
    stopReview () {

      if (!this.reviewAbort) return

      this.reviewAbort.abort()
      this.reviewAbort = null
    },
    imageSrc (image) {

      const value = String(image || '')

      if (!value) return ''
      if (value.startsWith('data:')) return value

      const mime = value.startsWith('iVBOR') ? 'image/png' : 'image/jpeg'

      return `data:${mime};base64,${value}`
    },
    openShotPicker () {

      if (this.$refs.shotInput) this.$refs.shotInput.click()
    },
    onPromptPaste (event) {

      const items = event.clipboardData && event.clipboardData.items

      if (!items) return

      const files = []

      for (const item of items) {

        if (!item.type || item.type.indexOf('image/') !== 0) continue

        const file = item.getAsFile()

        if (file) files.push(file)
      }

      if (!files.length) return

      event.preventDefault()
      this.addShotFiles(files)
    },
    onShotFiles (event) {

      const files = Array.from(event.target.files || [])

      event.target.value = ''
      this.addShotFiles(files)
    },
    addShotFiles (files) {

      for (const file of files) {

        if (!file || !file.type || file.type.indexOf('image/') !== 0) continue

        if (this.attachments.length >= 3) {

          this.error = 'You can attach up to 3 screenshots.'
          break
        }

        this.readShot(file)
      }
    },
    readShot (file) {

      const reader = new FileReader()

      reader.onload = () => {

        const data = String(reader.result || '').replace(/^data:image\/[a-z0-9.+-]+;base64,/i, '')

        if (!data || data.length > 1500000) {

          this.error = 'That screenshot is too large.'
          return
        }

        if (this.attachments.length >= 3) {

          this.error = 'You can attach up to 3 screenshots.'
          return
        }

        this.attachments.push(data)
      }

      reader.readAsDataURL(file)
    },
    removeAttachment (index) {

      this.attachments.splice(index, 1)
    },
    formatDate (dateStr) {

      if (!dateStr) return ''

      const date = new Date(dateStr)

      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    scrollToEnd () {

      this.$nextTick(() => {

        if (this.$refs.thread) this.$refs.thread.scrollTop = this.$refs.thread.scrollHeight

        const boxes = this.$refs.thoughtBox
        const box = Array.isArray(boxes) ? boxes[boxes.length - 1] : boxes

        if (box) box.scrollTop = box.scrollHeight
      })
    },
    async readBuildStream (response, turn) {

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {

        const chunk = await reader.read()

        if (chunk.done) {

          if (buffer.trim()) this.consumeBuildEvents(`${buffer}\n\n`, turn)
          break
        }

        buffer += decoder.decode(chunk.value, { stream: true })
        buffer = this.consumeBuildEvents(buffer, turn)
      }
    },
    consumeBuildEvents (buffer, turn) {

      const parts = buffer.split('\n\n')
      const rest = parts.pop()

      for (const part of parts) {

        const lines = part.split('\n')
        let eventName = 'message'
        let data = ''

        for (const line of lines) {

          if (line.startsWith('event:')) eventName = line.slice(6).trim()
          if (line.startsWith('data:')) data += line.slice(5).trim()
        }

        if (!data) continue

        const payload = JSON.parse(data)

        if (eventName === 'thinking') {

          turn.thinking += payload.text || ''
          turn.status = ''
          this.scrollToEnd()
        } else if (eventName === 'status') {

          turn.status = payload.text || ''
          this.scrollToEnd()
        } else if (eventName === 'action') {

          turn.actions.push(payload.action)
          this.scrollToEnd()
        } else if (eventName === 'screenshot') {

          this.attachScreenshot(turn, payload)
          this.scrollToEnd()
        } else if (eventName === 'done') {

          turn.answer = payload.answer
          turn.actions = this.keepShots(turn.actions, payload.actions || turn.actions)
          turn.thinking = payload.thinking || turn.thinking
          turn.waitingOnUser = payload.waitingOnUser === true
          turn.status = ''
          this.activeHistoryId = payload._id || this.activeHistoryId
          publishAiTokenUsage(payload.tokenUsage)
        } else if (eventName === 'error') {

          throw new Error(payload.message || 'The builder failed')
        }
      }

      return rest
    },
    async send () {

      if (!this.canSend || this.building) return

      this.stopReview()
      this.building = true
      this.error = null

      const images = this.attachments.slice()
      const currentPrompt = this.prompt.trim() || 'Look at the attached screenshot.'

      this.prompt = ''
      this.attachments = []
      this.conversation.push({ prompt: currentPrompt, images, answer: null, actions: [], thinking: '', status: '' })
      this.scrollToEnd()

      try {

        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build`,
          {
            method: 'POST',
            credentials: 'include',
            headers: Object.assign({
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream'
            }, this.headers()),
            body: JSON.stringify({
              prompt: currentPrompt,
              images,
              messages: this.sessionMessages,
              historyId: this.activeHistoryId,
              stream: true
            })
          }
        )

        if (!response.ok) {

          const text = await response.text()

          throw new Error(text || response.statusText)
        }

        const lastTurn = this.conversation[this.conversation.length - 1]
        const contentType = response.headers.get('content-type') || ''

        if (contentType.includes('text/event-stream')) {

          await this.readBuildStream(response, lastTurn)
        } else {

          const data = await response.json()

          lastTurn.answer = data.answer
          lastTurn.actions = data.actions || []
          lastTurn.thinking = data.thinking || ''
          lastTurn.waitingOnUser = data.waitingOnUser === true
          this.activeHistoryId = data._id || null
        }

        lastTurn.status = ''
        const attached = images.length
          ? `\n(${images.length} screenshot${images.length === 1 ? '' : 's'} attached.)`
          : ''
        this.sessionMessages.push({ role: 'user', content: `${currentPrompt}${attached}` })
        this.sessionMessages.push({ role: 'assistant', content: this.assistantContent(lastTurn) })

        await this.loadHistory()

        if (!lastTurn.waitingOnUser) this.reviewLastTurn(lastTurn)
      } catch (error) {

        this.conversation.pop()
        this.prompt = currentPrompt
        this.attachments = images
        this.error = error.message
      }

      this.building = false
      this.scrollToEnd()
    },
    reviewLastTurn (turn) {

      const controller = new AbortController()

      this.reviewAbort = controller
      turn.status = 'Checking whether that finished your request'
      this.scrollToEnd()
      this.runReview(turn, controller)
    },
    async runReview (turn, controller) {

      try {

        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/ai-build/review`,
          {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: Object.assign({
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream'
            }, this.headers()),
            body: JSON.stringify({
              prompt: turn.prompt,
              answer: turn.answer,
              thinking: turn.thinking,
              actions: (turn.actions || []).map((action) => ({
                tool: action.tool,
                ok: action.ok,
                summary: action.summary
              })),
              messages: this.sessionMessages,
              historyId: this.activeHistoryId,
              stream: true
            })
          }
        )

        if (!controller.signal.aborted && response.ok) {

          const contentType = response.headers.get('content-type') || ''

          if (contentType.includes('text/event-stream')) {

            await this.readBuildStream(response, turn)
          } else {

            const data = await response.json()

            turn.answer = data.answer
            turn.actions = data.actions || turn.actions
            turn.thinking = data.thinking || turn.thinking
            turn.waitingOnUser = data.waitingOnUser === true
            publishAiTokenUsage(data.tokenUsage)
          }

          if (this.reviewAbort === controller) {

            const last = this.sessionMessages[this.sessionMessages.length - 1]

            if (last && last.role === 'assistant') last.content = this.assistantContent(turn)
          }
        }
      } catch (error) {

        if (!(error && error.name === 'AbortError')) turn.status = ''
      }

      if (this.reviewAbort === controller || controller.signal.aborted) {

        turn.status = ''

        if (this.reviewAbort === controller) this.reviewAbort = null

        this.scrollToEnd()
      }
    }
  }
}
</script>

<style scoped>
.ai-build-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-build-context {
  color: #707070;
  font-size: 0.78rem;
  padding: 0 8px;
}

.ai-build-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.ai-build-sidebar {
  width: 25%;
  min-width: 200px;
  max-width: 320px;
  border-right: 1px dotted #474842;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 10px;
  flex-shrink: 0;
}

.sidebar-section-label {
  padding: 10px 12px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.new-chat-btn {
  background: none;
  border: 1px solid #333330;
  border-radius: 4px;
  color: #707070;
  font-size: 1rem;
  width: 24px;
  height: 24px;
  cursor: pointer;
  padding: 0;
}

.new-chat-btn:hover {
  background: #222220;
  color: #9ad12b;
  border-color: #484845;
}

.ai-build-history {
  flex: 1;
  overflow-y: auto;
  padding: 0 4px;
}

.ai-build-history-item {
  padding: 8px 10px;
  margin: 2px 0;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
}

.ai-build-history-item:hover {
  background: #1e1e1b;
}

.ai-build-history-item-active {
  background: #1a1a17;
  border-left: 2px solid #9ad12b;
}

.ai-build-history-prompt {
  font-size: 0.8rem;
  color: #c0c0be;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 18px;
}

.ai-build-history-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: #a8a8a4;
  margin-top: 3px;
}

.ai-build-history-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  color: #a8a8a4;
  font-size: 1rem;
  cursor: pointer;
  opacity: 0;
}

.ai-build-history-item:hover .ai-build-history-delete {
  opacity: 1;
}

.ai-build-history-empty {
  padding: 20px 12px;
  text-align: center;
  color: #3e3e3c;
  font-size: 0.78rem;
}

.ai-build-clear {
  margin: 4px 8px 8px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #333330;
  border-radius: 4px;
  color: #707070;
  font-size: 0.72rem;
  cursor: pointer;
}

.ai-build-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.ai-build-thread {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
}

.ai-build-empty {
  padding: 48px 16px;
  text-align: center;
  color: #707070;
  font-size: 0.9rem;
}

.ai-build-empty p {
  max-width: 560px;
  margin: 0 auto 16px;
  line-height: 1.5;
}

.ai-build-examples {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 640px;
  margin: 0 auto;
}

.ai-build-examples button {
  text-align: left;
  background: #1e1e1b;
  color: #c0c0be;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.82rem;
  cursor: pointer;
}

.ai-build-examples button:hover {
  border-color: #474842;
  color: #e8e8e6;
}

.ai-build-turn {
  margin-bottom: 20px;
}

.ai-build-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #707070;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.ai-build-label-ai {
  color: #9ad12b;
}

.ai-build-user-text,
.ai-build-answer {
  white-space: pre-wrap;
  font-size: 0.85rem;
  line-height: 1.5;
}

.ai-build-user-text {
  background: #1e1e1b;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 8px 12px;
  color: #e8e8e6;
  margin-bottom: 10px;
}

.ai-build-answer {
  color: #c0c0be;
}

.ai-build-thoughts {
  margin-bottom: 8px;
}

.ai-build-thoughts summary {
  cursor: pointer;
  color: #707070;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ai-build-thoughts pre {
  margin: 6px 0 0;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  color: #8d8d8a;
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.78rem;
  line-height: 1.45;
  font-family: inherit;
}

.ai-build-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.ai-build-action {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  padding: 6px 10px;
  color: #c0c0be;
  font-size: 0.8rem;
}

.ai-build-shot {
  flex: 1 0 100%;
  width: 100%;
  max-width: 480px;
  margin-top: 4px;
  border: 1px solid #2a2a27;
  border-radius: 4px;
}

.ai-build-action-failed {
  border-color: #5a2030;
  color: #f82471;
}

.ai-build-action-tool {
  flex-shrink: 0;
  color: #9ad12b;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ai-build-action-failed .ai-build-action-tool {
  color: #f82471;
}

.ai-build-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #707070;
  font-size: 0.85rem;
}

.ai-build-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #2a2a27;
  border-top-color: #9ad12b;
  border-radius: 50%;
  animation: ai-build-spin 0.7s linear infinite;
}

@keyframes ai-build-spin {
  to { transform: rotate(360deg); }
}

.ai-build-error {
  margin: 0 16px 8px;
  padding: 8px 12px;
  background: #2a1015;
  border: 1px solid #5a2030;
  border-radius: 4px;
  color: #f82471;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ai-build-error button {
  background: none;
  border: none;
  color: #f82471;
  font-size: 1.1rem;
  cursor: pointer;
}

.ai-build-user-shots {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.ai-build-user-shots img {
  max-width: 220px;
  border: 1px solid #2a2a27;
  border-radius: 4px;
}

.ai-build-prompt {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #2a2a27;
}

.ai-build-attachments {
  display: flex;
  gap: 8px;
}

.ai-build-attachment {
  position: relative;
}

.ai-build-attachment img {
  display: block;
  width: 72px;
  height: 54px;
  object-fit: cover;
  border: 1px solid #2a2a27;
  border-radius: 4px;
}

.ai-build-attachment button {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: #0f0f0d;
  color: #e8e8e6;
  line-height: 16px;
  cursor: pointer;
}

.ai-build-prompt-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.ai-build-prompt textarea {
  flex: 1;
  background: #1e1e1b;
  color: #e8e8e6;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
}

.ai-build-prompt textarea:focus {
  border-color: #474842;
}

.ai-build-prompt-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-build-file {
  display: none;
}

.ai-build-attach,
.ai-build-send {
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 16px;
  cursor: pointer;
}

.ai-build-attach {
  background: transparent;
  border: 1px solid #3a3a36;
  color: #c0c0be;
}

.ai-build-send {
  background: #9ad12b;
  border: none;
  color: #0f0f0d;
}

.ai-build-attach:disabled,
.ai-build-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
